import React, { useState, useEffect, useCallback } from 'react';
import { format, subDays } from 'date-fns';
import { getAnalysisOverview, getBidderAnalysis } from '../services/api';
import StatsCards from '../components/StatsCards';
import BidderTable from '../components/BidderTable';
import { WinRateChart, AvgCpmChart, RevenueShareChart, ResponseTimeVsCpmChart } from '../components/Charts';
import IssuesList from '../components/IssuesList';
import DateRangePicker from '../components/DateRangePicker';

function PublisherDashboard() {
  const [overview, setOverview] = useState(null);
  const [bidders, setBidders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState({
    from: format(subDays(new Date(), 7), 'yyyy-MM-dd'),
    to: format(new Date(), 'yyyy-MM-dd'),
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [overviewRes, biddersRes] = await Promise.all([
        getAnalysisOverview(dateRange),
        getBidderAnalysis(dateRange),
      ]);
      setOverview(overviewRes.data);
      setBidders(biddersRes.data.bidders);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load analysis data');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) return <div className="loading">Loading analysis...</div>;
  if (error) return <div className="error-msg">{error}</div>;

  return (
    <div>
      <div className="section-header">
        <h2>Dashboard</h2>
        <DateRangePicker from={dateRange.from} to={dateRange.to} onChange={setDateRange} />
      </div>

      <StatsCards data={overview} />

      <h3 style={{ marginBottom: 12 }}>Bidder Performance</h3>
      <BidderTable bidders={bidders} />

      <div className="charts-row">
        <WinRateChart bidders={overview?.winRates?.bidders} />
        <AvgCpmChart bidders={overview?.winRates?.bidders} />
      </div>

      <div className="charts-row">
        <ResponseTimeVsCpmChart
          latencyBidders={overview?.latency?.bidders}
          winBidders={overview?.winRates?.bidders}
        />
        <RevenueShareChart bidders={overview?.winRates?.bidders} />
      </div>

      <IssuesList
        configIssues={overview?.configIssues}
        revenueLeakage={overview?.revenueLeakage}
      />
    </div>
  );
}

export default PublisherDashboard;
